import React, { useRef } from 'react';
import classnames from 'classnames';
import {
  Controller,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import IntlTelInput from '@intl-tel-input/react';
import 'intl-tel-input/styles';
import { useMutation } from '@tanstack/react-query';

import { loadUserProfile } from '../../api/Auth';
import { APIupdateProfile } from '../../api/User';

import Notices from '../Notices';

import { getUserProfile } from '../../helpers/auth';
import { setNotice } from '../../helpers/notice';

import type { PlanKey } from '../../api/Plan';
import type { ProfileFormData } from '../../types/forms/profileform';
import type { User } from '../../types/user';

import '@awesome.me/webawesome/dist/components/select/select.js';

const AccountProfileEdit: React.FC<{ profile: User }> = ({ profile }) => {
  const phoneFieldRef = useRef<any>(null);
  const loggedUser = getUserProfile();
  const isSelfEdit = loggedUser?.id === profile.id;
  const canSetPassword = Boolean(loggedUser?.is_administrator) && !isSelfEdit;
  const canChangePlan = Boolean(loggedUser?.is_administrator);

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: async (requestData: ProfileFormData): Promise<ProfileFormData> => {
      const response = await APIupdateProfile(profile.id, requestData);
      return response.data;
    },
    onError: (error) => {
      setNotice('update.failed', error.message);
    },
    onSuccess: () => {
      resetField('password');
      resetField('password_confirmation');
      if (isSelfEdit) {
        setNotice('profile.updated');
      } else {
        setNotice('user.updated');
      }
      loadUserProfile();
    },
  });

  const schema = z.object({
    name: z.string().min(1, { message: "Name is required" }),
    email: z.email().min(1, { message: "Email is required" }),
    phone: z.string().optional().refine((value) => {
      if (!value) return true;
      if (phoneFieldRef.current) {
        const iti = phoneFieldRef.current.getInstance();
        return iti.isValidNumber();
      }
      return false;
    }, { message: "Invalid phone number format" }),
    phoneOptIn: z.boolean().optional(),
    plan_key: z.enum(['free', 'lite', 'standard', 'forever']).optional(),
    password: z.string().optional().refine((value) => !value || value.length >= 8, {
      message: 'Password must be at least 8 characters long',
    }),
    password_confirmation: z.string().optional(),
  }).refine((data) => (data.password || '') === (data.password_confirmation || ''), {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

  const {
    control,
    register,
    formState: {
      errors,
    },
    handleSubmit,
    resetField,
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: profile.name || '',
      email: profile.email || '',
      phone: profile.phone || '',
      phoneOptIn: profile.phone_opt_in || false,
      plan_key: profile.plan_key ?? 'free',
      password: '',
      password_confirmation: '',
    },
    resolver: zodResolver(schema) as any,
  });

  const submit = (data: ProfileFormData) => {
    setNotice(null);
    const { password, password_confirmation, plan_key, ...profileData } = data;
    const updateData = {
      ...profileData,
      ...(canChangePlan ? { plan_key: plan_key ?? 'free' } : {}),
    };
    updateProfile(canSetPassword && password ? { ...updateData, password, password_confirmation } : updateData);
  };

  return (
    <div className="account-content account-profile-content">
      <Notices />
      <form onSubmit={handleSubmit(submit)} className="profile-form" noValidate>
        <wa-input
          label="Name"
          className={errors.name ? 'invalid' : ''}
          hint={errors.name ? errors.name.message : ''}
          required
          defaultValue={profile?.name}
          {...register("name")}
        />
        <wa-input
          label="E-mail"
          className={classnames({ 'invalid': errors.email, 'hidden': isSelfEdit })}
          hint={errors.email ? errors.email.message : ''}
          required
          defaultValue={profile?.email}
          {...register("email")}
        />
        {canChangePlan && (
          <Controller
            control={control}
            name="plan_key"
            render={({ field }) => (
              <wa-select
                label="Plan"
                value={field.value ?? 'free'}
                onChange={(event: any) => field.onChange(event.target.value as PlanKey)}
              >
                <wa-option value="free">Free</wa-option>
                <wa-option value="lite">Lite</wa-option>
                <wa-option value="standard">Standard</wa-option>
                <wa-option value="forever">Forever</wa-option>
              </wa-select>
            )}
          />
        )}
        <label htmlFor="phone" className="label">Phone number</label>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <div className={`intl-tel-input-container ${errors.phone ? 'invalid' : ''}`}>
              <IntlTelInput
                countryOrder={["us","ca"]}
                initialCountry="us"
                loadUtils={() => import('intl-tel-input/utils')}
                ref={phoneFieldRef}
                inputProps={{ id: 'phone', defaultValue: profile?.phone }}
                onChangeNumber={(number) => {
                  field.onChange(number);
                }}
              />
              {errors.phone && <p className="error-message">{errors.phone.message}</p>}
            </div>
          )}
        />
        <div className="checkbox-container">
          <label htmlFor="phoneOptIn">
            <input type="checkbox" id="phoneOptIn" {...register('phoneOptIn')} /> I agree to receive SMS notifications
          </label>
        </div>
        {canSetPassword && (
          <div className="profile-password-fields">
            <p className="profile-password-note">
              Leave the password fields empty to keep the current password.
            </p>
            <wa-input
              label="New Password"
              type="password"
              autocomplete="new-password"
              className={errors.password ? 'invalid' : ''}
              hint={errors.password ? errors.password.message : ''}
              {...register("password")}
            />
            <wa-input
              label="Confirm New Password"
              type="password"
              autocomplete="new-password"
              className={errors.password_confirmation ? 'invalid' : ''}
              hint={errors.password_confirmation ? errors.password_confirmation.message : ''}
              {...register("password_confirmation")}
            />
          </div>
        )}
        <wa-button type="submit">
          {isPending ? 'Saving...' : 'Save'}
        </wa-button>
      </form>
    </div>
  );
}

export default AccountProfileEdit;
